import { useId, useMemo, useRef, useState } from 'react';
import { Check, ChevronDown, Search, X } from 'lucide-react';

export interface SearchableOption {
  id: string;
  label: string;
  description?: string;
  disabled?: boolean;
}

type SearchableSelectProps = {
  options: SearchableOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  isLoading?: boolean;
  emptyMessage?: string;
  clearable?: boolean;
  id?: string;
  className?: string;
  inputClassName?: string;
  name?: string;
  required?: boolean;
  'aria-label'?: string;
};

export function SearchableSelect({
  options,
  value,
  onChange,
  placeholder = 'Search...',
  disabled = false,
  isLoading = false,
  emptyMessage = 'No options found',
  clearable = true,
  id,
  className = '',
  inputClassName = '',
  name,
  required = false,
  'aria-label': ariaLabel,
}: SearchableSelectProps) {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const listboxId = `${inputId}-listbox`;
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');

  const selectedOption = useMemo(
    () => options.find((option) => option.id === value),
    [options, value],
  );

  const filteredOptions = useMemo(() => {
    const searchTerm = query.trim().toLowerCase();
    if (!searchTerm) {
      return options;
    }

    return options.filter((option) => (
      option.label.toLowerCase().includes(searchTerm) ||
      option.description?.toLowerCase().includes(searchTerm) ||
      option.id.toLowerCase().includes(searchTerm)
    ));
  }, [options, query]);

  const close = () => {
    setIsOpen(false);
    setQuery('');
  };

  const isDisabled = disabled || isLoading;
  const displayValue = isOpen ? query : selectedOption?.label ?? '';

  return (
    <div
      ref={wrapperRef}
      className={`searchable-select ${className}`}
      onBlur={(event) => {
        if (!wrapperRef.current?.contains(event.relatedTarget as Node | null)) {
          close();
        }
      }}
    >
      {name ? <input type="hidden" name={name} value={value} required={required} /> : null}
      <div className="searchable-select-control">
        <Search className="searchable-select-search-icon" size={16} />
        <input
          id={inputId}
          type="text"
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={isOpen}
          aria-controls={listboxId}
          aria-label={ariaLabel}
          className={`searchable-select-input ${inputClassName}`}
          value={displayValue}
          placeholder={selectedOption ? 'Search options...' : placeholder}
          disabled={isDisabled}
          onFocus={() => {
            setIsOpen(true);
            setQuery('');
          }}
          onChange={(event) => {
            setQuery(event.target.value);
            setIsOpen(true);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Escape') {
              close();
            }
          }}
        />
        <div className="searchable-select-actions">
          {clearable && value && !isLoading ? (
            <button
              type="button"
              className="searchable-select-icon-button"
              onClick={() => {
                onChange('');
                close();
              }}
              disabled={disabled}
              aria-label="Clear selection"
            >
              <X size={14} />
            </button>
          ) : null}
          <button
            type="button"
            className="searchable-select-icon-button"
            disabled={isDisabled}
            onClick={() => {
              setIsOpen((current) => !current);
              setQuery('');
            }}
            aria-label={isOpen ? 'Close options' : 'Open options'}
          >
            <ChevronDown className={isOpen ? 'searchable-select-chevron-open' : ''} size={16} />
          </button>
        </div>
      </div>

      {isOpen ? (
        <div id={listboxId} role="listbox" className="searchable-select-options">
          {filteredOptions.length === 0 ? (
            <div className="searchable-select-empty">{isLoading ? 'Loading...' : emptyMessage}</div>
          ) : (
            filteredOptions.map((option) => {
              const selected = option.id === value;
              return (
                <button
                  key={option.id}
                  type="button"
                  role="option"
                  aria-selected={selected}
                  disabled={option.disabled}
                  className={`searchable-select-option ${selected ? 'searchable-select-option-selected' : ''}`}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => {
                    if (!option.disabled) {
                      onChange(option.id);
                      close();
                    }
                  }}
                >
                  <span className="searchable-select-option-copy">
                    <span>{option.label}</span>
                    {option.description ? <small>{option.description}</small> : null}
                  </span>
                  {selected ? <Check size={16} /> : null}
                </button>
              );
            })
          )}
        </div>
      ) : null}
    </div>
  );
}
